import { useCallback, useMemo, useState, MouseEvent } from "react"
import { GetApp } from "../../App"
import { BlockRow } from "../../Components/Block"
import { CloseButton } from "../../Components/CloseButton"
import { ConfirmDialog } from "../../Components/ConfirmDialog"
import { INoteAttachment, INoteAttachmentResponse, ApiClient, NoteApi } from "../../Types"
import { NoteAttachmentsProps } from "./Params"

export const NoteAttachmentsData = {
  visibleAttachments(attachments: INoteAttachment[] | undefined, removedIds: number[]): INoteAttachment[] {
    return (attachments || []).filter((a) => removedIds.indexOf(a.id) < 0);
  },
  removeAttachment(api: ApiClient, shipId: number, noteId: number, attachmentId: number): Promise<INoteAttachmentResponse> {
    return new NoteApi(api).noteAttachments.delete({ params: { shipId, noteId, attachmentId } }).call();
  },
  attachmentUrl(api: ApiClient, shipId: number, noteId: number, attachment: INoteAttachment, cloudSession: boolean): string {
    return cloudSession || attachment.useUrl
      ? (attachment.url || "")
      : new NoteApi(api).noteAttachments.download({ params: { shipId, noteId, attachmentId: attachment.id } }).url();
  },
  isImage(attachment: INoteAttachment): boolean {
    return attachment.contentType.startsWith("image/");
  },
};

export function NoteAttachments({ attachments, shipId, noteId, editable, cloudSession, removeConfirm, removeAttachment }: NoteAttachmentsProps) {
  const app = GetApp();
  const [removedIds, setRemovedIds] = useState<number[]>([]);
  const [pendingRemovalAttachmentId, setPendingRemovalAttachmentId] = useState<number | undefined>(undefined);
  const visible = useMemo(() => NoteAttachmentsData.visibleAttachments(attachments, removedIds), [attachments, removedIds]);

  const requestRemove = useCallback((attachmentId: number) => (event?: MouseEvent<HTMLDivElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setPendingRemovalAttachmentId(attachmentId);
  }, []);

  const cancelRemove = useCallback(() => setPendingRemovalAttachmentId(undefined), []);

  const confirmRemove = useCallback(async () => {
    if (pendingRemovalAttachmentId == undefined) {
      return;
    }
    // Let a failed request propagate so the dialog can keep itself open and show the error;
    // only dismiss the dialog once the removal has actually succeeded.
    await (removeAttachment
      ? removeAttachment(pendingRemovalAttachmentId)
      : NoteAttachmentsData.removeAttachment(app.apiClient, shipId, noteId, pendingRemovalAttachmentId));
    setRemovedIds((ids) => [...ids, pendingRemovalAttachmentId]);
    setPendingRemovalAttachmentId(undefined);
  }, [pendingRemovalAttachmentId, shipId, noteId, app, removeAttachment]);

  if (visible.length < 1) {
    return null;
  }

  return (
    <BlockRow className="NoteView-attachments">
      {visible.map((a) => {
        const key = `note-${noteId}-attachment-${a.id}`;
        const url = NoteAttachmentsData.attachmentUrl(app.apiClient, shipId, noteId, a, cloudSession);
        return (
          <a key={key} href={url} className="attachment" target="_blank" rel="noreferrer">
            {editable && (
              <CloseButton className="attachment-remove" onClick={requestRemove(a.id)} />
            )}
            {NoteAttachmentsData.isImage(a) && <img src={url} />}
            <span className="attachment-label">{a.fileName}.{a.fileType}</span>
          </a>
        );
      })}
      {pendingRemovalAttachmentId != undefined && (
        <ConfirmDialog
          awaitConfirm={true}
          title={removeConfirm.title}
          message={removeConfirm.message}
          confirmLabel={removeConfirm.confirmLabel}
          cancelLabel={removeConfirm.cancelLabel}
          errorMessage={removeConfirm.errorMessage}
          style="alarm"
          onConfirm={confirmRemove}
          onCancel={cancelRemove}
        />
      )}
    </BlockRow>
  );
}
